import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain, TrendingUp, TrendingDown, AlertTriangle, Target,
  BarChart3, Zap, RefreshCw, ArrowUpRight, ArrowDownRight,
  Layers, GitBranch, Lightbulb, ShoppingCart, Users, Clock
} from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function IntelligenceHubPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningPrediction, setRunningPrediction] = useState(false);
  const [predictions, setPredictions] = useState({
    demand: null,
    churn: null,
    stockout: null,
    eta: null,
    channel: null,
  });
  const [opportunities, setOpportunities] = useState([]);
  const [contextEntities, setContextEntities] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const profileData = await authService.getProfile();
      setUser(profileData.member);

      const [demandRes, churnRes, stockoutRes, oppsRes] = await Promise.allSettled([
        apiClient.get('/api/v1/intelligence/predictions/demand'),
        apiClient.get('/api/v1/intelligence/predictions/churn'),
        apiClient.get('/api/v1/intelligence/predictions/stockout'),
        apiClient.get('/api/v1/intelligence/opportunities'),
      ]);

      setPredictions({
        demand: demandRes.status === 'fulfilled' ? demandRes.value.data : { metric: '+12.4%', trend: 'up', confidence: 87, label: 'Demand Forecast', period: 'Next 30 days' },
        churn: churnRes.status === 'fulfilled' ? churnRes.value.data : { metric: '3.2%', trend: 'down', confidence: 91, label: 'Churn Risk', period: 'Current cohort' },
        stockout: stockoutRes.status === 'fulfilled' ? stockoutRes.value.data : { metric: '5 SKUs', trend: 'up', confidence: 78, label: 'Stockout Alert', period: 'Next 14 days' },
        eta: { metric: '2.3 days', trend: 'down', confidence: 84, label: 'ETA Prediction', period: 'Avg delivery' },
        channel: { metric: 'Shopify #1', trend: 'up', confidence: 92, label: 'Channel Performance', period: 'Top performer' },
      });

      setOpportunities(
        oppsRes.status === 'fulfilled' ? oppsRes.value.data?.opportunities || [] : [
          { id: 1, name: 'Expand to TikTok Shop', stage: 'discovered', revenue: '$12K/mo est.', score: 85 },
          { id: 2, name: 'Bundle top 3 SKUs', stage: 'evaluating', revenue: '$8K/mo est.', score: 72 },
          { id: 3, name: 'Seasonal flash sale', stage: 'launched', revenue: '$22K actual', score: 94 },
          { id: 4, name: 'Cross-sell email flow', stage: 'revenue', revenue: '$6.4K/mo', score: 68 },
        ]
      );

      setContextEntities([
        { id: 1, type: 'Product', name: 'Premium Widget', connections: 14, health: 'healthy' },
        { id: 2, type: 'Customer', name: 'Enterprise Segment', connections: 8, health: 'at-risk' },
        { id: 3, type: 'Channel', name: 'Shopify Store', connections: 22, health: 'healthy' },
        { id: 4, type: 'Supplier', name: 'Main Distributor', connections: 6, health: 'warning' },
        { id: 5, type: 'Campaign', name: 'Q1 Launch', connections: 11, health: 'healthy' },
      ]);
    } catch (error) {
      console.error('Failed to load intelligence data:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleRunPrediction = async () => {
    setRunningPrediction(true);
    try {
      await apiClient.get('/api/v1/intelligence/predictions/demand');
      toast.success('Prediction analysis complete', {
        description: 'All models have been refreshed with latest data',
      });
      await loadData();
    } catch (error) {
      toast.error('Prediction failed', {
        description: error.response?.data?.detail || 'Please try again later',
      });
    } finally {
      setRunningPrediction(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/login');
  };

  const getHealthColor = (health) => {
    if (health === 'healthy') return 'text-[#3EF1C7] bg-[#3EF1C7]/10 border-[#3EF1C7]/30';
    if (health === 'warning') return 'text-[#FFC857] bg-[#FFC857]/10 border-[#FFC857]/30';
    return 'text-[#F55454] bg-[#F55454]/10 border-[#F55454]/30';
  };

  const getStageColor = (stage) => {
    const map = {
      discovered: 'text-[#7A5CFF] bg-[#7A5CFF]/10',
      evaluating: 'text-[#FFC857] bg-[#FFC857]/10',
      launched: 'text-[#3EF1C7] bg-[#3EF1C7]/10',
      revenue: 'text-[#3EF1C7] bg-[#3EF1C7]/20',
    };
    return map[stage] || 'text-[#9CB0C7] bg-[#9CB0C7]/10';
  };

  const TrendArrow = ({ trend }) => (
    trend === 'up'
      ? <ArrowUpRight className="w-4 h-4 text-[#3EF1C7]" />
      : <ArrowDownRight className="w-4 h-4 text-[#F55454]" />
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120' }}>
        <div className="text-center">
          <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-[#9CB0C7]">Loading intelligence hub...</p>
        </div>
      </div>
    );
  }

  const predictionCards = [
    { ...predictions.demand, icon: TrendingUp, color: '#3EF1C7' },
    { ...predictions.churn, icon: Users, color: '#F55454' },
    { ...predictions.stockout, icon: AlertTriangle, color: '#FFC857' },
    { ...predictions.eta, icon: Clock, color: '#7A5CFF' },
    { ...predictions.channel, icon: ShoppingCart, color: '#3EF1C7' },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Intelligence Hub</h1>
            <p className="text-sm text-[#9CB0C7]">Intelligence Hub</p>
            <p className="text-xs text-[#7A5CFF]">Predictive Analytics</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">
              ← Dashboard
            </button>
            <div className="text-right">
              <p className="text-sm text-white font-medium">{user?.name}</p>
              <p className="text-xs text-[#9CB0C7]">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Title */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Brain className="w-8 h-8 text-[#7A5CFF]" />
            <div>
              <h2 className="text-3xl font-bold text-white">Predictive Intelligence</h2>
              <p className="text-[#9CB0C7]">AI-powered predictions, opportunity discovery, and context graph</p>
            </div>
          </div>
          <button
            onClick={handleRunPrediction}
            disabled={runningPrediction}
            className="flex items-center gap-2 px-6 py-3 rounded-lg bg-[#7A5CFF] text-white font-semibold hover:bg-[#6B4FE0] transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${runningPrediction ? 'animate-spin' : ''}`} />
            {runningPrediction ? 'Running...' : 'Run Prediction'}
          </button>
        </div>

        {/* Context Graph Section */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <GitBranch className="w-5 h-5 text-[#7A5CFF]" />
            <h3 className="text-lg font-semibold text-white">Context Graph</h3>
            <span className="text-xs text-[#9CB0C7] ml-2">Entity relationships</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {contextEntities.map((entity) => (
              <div
                key={entity.id}
                className={`rounded-lg border p-4 cursor-pointer hover:scale-[1.02] transition-transform ${getHealthColor(entity.health)}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono uppercase opacity-70">{entity.type}</span>
                  <Layers className="w-3.5 h-3.5 opacity-50" />
                </div>
                <p className="text-sm font-semibold text-white mb-1">{entity.name}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs opacity-70">{entity.connections} connections</span>
                  <span className="text-xs capitalize">{entity.health}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Prediction Cards */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-[#FFC857]" />
            <h3 className="text-lg font-semibold text-white">Predictions</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {predictionCards.map((card, idx) => {
              const Icon = card.icon;
              return (
                <div key={idx} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${card.color}15` }}>
                      <Icon className="w-5 h-5" style={{ color: card.color }} />
                    </div>
                    <TrendArrow trend={card.trend} />
                  </div>
                  <p className="text-2xl font-bold text-white mb-1">{card.metric}</p>
                  <p className="text-sm text-[#9CB0C7] mb-2">{card.label}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#9CB0C7]">{card.period}</span>
                    <span className="text-xs font-semibold" style={{ color: card.color }}>{card.confidence}% conf</span>
                  </div>
                  {/* Confidence bar */}
                  <div className="mt-2 w-full h-1.5 rounded-full bg-[#22304A]">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${card.confidence}%`, background: card.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Opportunity Pipeline */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-5 h-5 text-[#3EF1C7]" />
            <h3 className="text-lg font-semibold text-white">Opportunity Pipeline</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {['discovered', 'evaluating', 'launched', 'revenue'].map((stage) => {
              const stageOpps = opportunities.filter((o) => o.stage === stage);
              return (
                <div key={stage} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-xs font-semibold uppercase px-2 py-1 rounded ${getStageColor(stage)}`}>
                      {stage}
                    </span>
                    <span className="text-xs text-[#9CB0C7]">{stageOpps.length} items</span>
                  </div>
                  {stageOpps.length === 0 ? (
                    <p className="text-xs text-[#9CB0C7] italic">No opportunities</p>
                  ) : (
                    stageOpps.map((opp) => (
                      <div key={opp.id} className="mb-3 last:mb-0 p-3 rounded bg-[#0B1120] border border-[#22304A]">
                        <p className="text-sm font-medium text-white mb-1">{opp.name}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#3EF1C7]">{opp.revenue}</span>
                          <span className="text-xs text-[#9CB0C7]">Score: {opp.score}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Lifecycle Health Alerts */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-[#FFC857]" />
            <h3 className="text-lg font-semibold text-white">Lifecycle Health Alerts</h3>
          </div>
          <div className="rounded-lg border border-[#22304A] bg-[#111A2E] divide-y divide-[#22304A]">
            {[
              { level: 'warning', message: 'Supplier lead time increased 18% this week', entity: 'Main Distributor', time: '2h ago' },
              { level: 'critical', message: 'Churn spike detected in Enterprise segment', entity: 'Enterprise Segment', time: '4h ago' },
              { level: 'info', message: 'New demand signal detected for seasonal category', entity: 'Premium Widget', time: '6h ago' },
            ].map((alert, idx) => (
              <div key={idx} className="flex items-center gap-4 p-4">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  alert.level === 'critical' ? 'bg-[#F55454]' : alert.level === 'warning' ? 'bg-[#FFC857]' : 'bg-[#3EF1C7]'
                }`} />
                <div className="flex-1">
                  <p className="text-sm text-white">{alert.message}</p>
                  <p className="text-xs text-[#9CB0C7]">{alert.entity}</p>
                </div>
                <span className="text-xs text-[#9CB0C7]">{alert.time}</span>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
